import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Link,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Avatar,
  LinearProgress,
  Tooltip,
  Badge,
  Container,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import SpeedIcon from "@mui/icons-material/Speed";
import ApiIcon from "@mui/icons-material/Api";
import TimerIcon from "@mui/icons-material/Timer";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";
import axios from "axios";

// Enhanced color palette
const COLORS = {
  primary: {
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    main: "#667eea",
    dark: "#5a67d8",
  },
  success: {
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    main: "#10b981",
    light: "#d1fae5",
    bg: "#ecfdf5",
  },
  error: {
    gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    main: "#ef4444",
    light: "#fee2e2",
    bg: "#fef2f2",
  },
  warning: {
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    main: "#f59e0b",
    light: "#fef3c7",
    bg: "#fffbeb",
  },
  info: {
    gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    main: "#3b82f6",
    light: "#dbeafe",
    bg: "#eff6ff",
  },
};

// Styled Components
const StyledHeaderCard = styled(Card)(({ theme }) => ({
  background: COLORS.primary.gradient,
  color: "white",
  borderRadius: 20,
  boxShadow: "0 20px 40px rgba(102, 126, 234, 0.3)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    background: "rgba(255, 255, 255, 0.15)",
    borderRadius: "50%",
  },
}));

const StyledStatsCard = styled(Card)(({ theme, status }) => ({
  borderRadius: 16,
  background: status === "healthy" 
    ? COLORS.success.gradient
    : COLORS.error.gradient,
  color: "white",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: status === "healthy" 
    ? "0 8px 25px rgba(16, 185, 129, 0.25)"
    : "0 8px 25px rgba(239, 68, 68, 0.25)",
  "&:hover": {
    transform: "translateY(-6px) scale(1.02)",
    boxShadow: status === "healthy" 
      ? "0 15px 35px rgba(16, 185, 129, 0.35)"
      : "0 15px 35px rgba(239, 68, 68, 0.35)",
  },
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.08)",
  border: "1px solid rgba(226, 232, 240, 0.5)",
  background: "#ffffff",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.875rem",
    color: "#374151",
    borderBottom: "2px solid #e5e7eb",
    padding: theme.spacing(2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    transform: "translateX(4px)",
    boxShadow: "4px 0 12px rgba(59, 130, 246, 0.1)",
  },
  "& .MuiTableCell-root": {
    borderBottom: "1px solid #f1f5f9",
    padding: theme.spacing(1.5, 2),
  },
}));

const StatusDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await axios.get("https://api.medstown.com/admin/check-services");

      const grouped = {
        title: "System Status",
        allHealthy: res.data.every(
          (api) => api.status === "UP" || api.status === "OK"
        ),
        totalApis: res.data.filter((api) => api.apiName).length,
        healthyApis: res.data.filter(
          (api) => api.apiName && (api.status === "UP" || api.status === "OK")
        ).length,
        avgResponseTime: Math.round(
          res.data.reduce((sum, api) => sum + (api.responseTimeMs || 0), 0) / res.data.length
        ),
        apis: res.data
          .filter((api) => api.apiName)
          .map((api) => ({
            name: api.apiName || "Unknown API",
            url: api.url || "",
            status: api.status || "UNKNOWN",
            responseTime: api.responseTimeMs || 0,
            lastChecked: api.lastChecked
              ? new Date(api.lastChecked).toLocaleString()
              : "Just now",
            details:
              api.details === "State: 1"
                ? "All systems operational"
                : api.details && api.details.trim() !== ""
                ? api.details
                : "Service running normally",
          })),
      };

      setData([grouped]);
    } catch (error) {
      console.error("Failed to load status:", error);
      setData([]);
    } finally {
      setLoading(false);
      if (isRefresh) setRefreshing(false);
    }
  };
useEffect(() => {
  fetchStatus(); // fetch once on initial mount
}, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress 
            size={60} 
            sx={{ 
              mb: 3,
              color: COLORS.primary.main,
            }} 
          />
          <Typography variant="h6" color="text.secondary">
            Loading System Status...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: 8 }}>
      {data.map((section) => (
        <Fade in={true} timeout={800} key={section.title}>
          <Box>
            {/* Header Card */}
            <StyledHeaderCard sx={{ mb: 4 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        width: 56,
                        height: 56,
                      }}
                    >
                      <ApiIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight={700}>
                        {section.title}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        Real-time monitoring of all system services
                      </Typography>
                    </Box>
                  </Box>
                  {/* <Tooltip title="Refresh Status">
                    <Badge 
                      color="success" 
                      variant="dot" 
                      invisible={!section.allHealthy}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.1)",
                          cursor: "pointer",
                          "&:hover": { 
                            bgcolor: "rgba(255, 255, 255, 0.2)",
                            transform: "scale(1.1)",
                          },
                        }}
                        onClick={() => fetchStatus(true)}
                      >
                        <RefreshIcon 
                          sx={{ 
                            fontSize: 24,
                            animation: refreshing ? "spin 1s linear infinite" : "none",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }} 
                        />
                      </Avatar>
                    </Badge>
                  </Tooltip> */}
                </Box>
              </CardContent>
            </StyledHeaderCard>

            {/* Main Stats Cards - Equal Heights */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <StyledStatsCard 
                  status={section.allHealthy ? "healthy" : "unhealthy"}
                  sx={{ minHeight: 120, height: 120 }}
                >
                  <CardContent sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 3,
                  }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          width: 56,
                          height: 56,
                        }}
                      >
                        {section.allHealthy ? (
                          <VerifiedIcon sx={{ color: "white", fontSize: 32 }} />
                        ) : (
                          <ErrorOutlineIcon sx={{ color: "white", fontSize: 32 }} />
                        )}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="white">
                          {section.allHealthy ? "All Systems Operational" : "Issues Detected"}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
                          Overall system health status
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </StyledStatsCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    borderRadius: 3, 
                    background: COLORS.info.gradient,
                    color: "white",
                    boxShadow: "0 8px 25px rgba(59, 130, 246, 0.25)",
                    minHeight: 120,
                    height: 120,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 30px rgba(59, 130, 246, 0.35)",
                    },
                  }}
                >
                  <CardContent sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between',
                    p: 3,
                  }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
                        <ApiIcon sx={{ color: "white" }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" fontWeight={700}>
                          {section.healthyApis}/{section.totalApis}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
                          APIs Online
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(section.healthyApis / section.totalApis) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        marginTop:0.2,
                        bgcolor: "rgba(255,255,255,0.2)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "white",
                          borderRadius: 4,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Additional Stats - Equal Heights */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, minHeight: 120, height: 120 }}>
                  <CardContent sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 2.5,
                  }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: COLORS.success.main, width: 48, height: 48 }}>
                        <SpeedIcon sx={{ color: "white" }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color={COLORS.success.main}>
                          {section.avgResponseTime}ms
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Average Response Time
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, minHeight: 120, height: 120 }}>
                  <CardContent sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 2.5,
                  }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: COLORS.warning.main, width: 48, height: 48 }}>
                        <TimerIcon sx={{ color: "white" }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color={COLORS.warning.main}>
                          Live
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Auto Refresh: 30s
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Status Table */}
            <StyledTableContainer>
              <Table size="medium">
                <StyledTableHead>
                  <TableRow>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ApiIcon sx={{ fontSize: 18 }} />
                        API Service
                      </Box>
                    </TableCell>
                    <TableCell>Endpoint URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Response Time</TableCell>
                    <TableCell>Last Checked</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {section.apis.map((api, index) => (
                    <Fade in={true} timeout={400 + index * 100} key={index}>
                      <StyledTableRow>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar
                              sx={{
                                bgcolor: api.status === "OK" || api.status === "UP" 
                                  ? COLORS.success.bg : COLORS.error.bg,
                                color: api.status === "OK" || api.status === "UP" 
                                  ? COLORS.success.main : COLORS.error.main,
                                width: 36,
                                height: 36,
                                fontSize: 14,
                                fontWeight: 700,
                              }}
                            >
                              {api.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>
                              {api.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {api.url ? (
                            <Link
                              href={
                                api.url.startsWith("http")
                                  ? api.url
                                  : `https://${api.url}`
                              }
                              target="_blank"
                              rel="noopener"
                              underline="hover"
                              sx={{ 
                                fontWeight: 500,
                                color: COLORS.primary.main,
                                "&:hover": { color: COLORS.primary.dark },
                              }}
                            >
                              {api.url.length > 40
                                ? api.url.slice(0, 40) + "..."
                                : api.url}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not available
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={api.status === "OK" || api.status === "UP" ? "HEALTHY" : api.status}
                            size="small"
                            icon={
                              api.status === "OK" || api.status === "UP" ? (
                                <VerifiedIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ErrorIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            sx={{
                              backgroundColor:
                                api.status === "OK" || api.status === "UP"
                                  ? COLORS.success.bg
                                  : COLORS.error.bg,
                              color:
                                api.status === "OK" || api.status === "UP"
                                  ? COLORS.success.main
                                  : COLORS.error.main,
                              fontWeight: 700,
                              borderRadius: 2,
                              border: `1px solid ${
                                api.status === "OK" || api.status === "UP"
                                  ? COLORS.success.main
                                  : COLORS.error.main
                              }`,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <SpeedIcon 
                              sx={{ 
                                fontSize: 16, 
                                color: api.responseTime > 1000 
                                  ? COLORS.error.main 
                                  : api.responseTime > 500
                                  ? COLORS.warning.main
                                  : COLORS.success.main
                              }} 
                            />
                            <Typography 
                              variant="body2" 
                              fontWeight={600}
                              color={
                                api.responseTime > 1000 
                                  ? COLORS.error.main 
                                  : api.responseTime > 500
                                  ? COLORS.warning.main
                                  : COLORS.success.main
                              }
                            >
                              {api.responseTime}ms
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {api.lastChecked}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={api.details || "Service operating normally"}>
                            <Chip
                              label={api.details && api.details.length > 20
                                ? api.details.slice(0, 20) + "..."
                                : api.details || "All Good"}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: COLORS.success.main,
                                color: COLORS.success.main,
                                backgroundColor: COLORS.success.light,
                                fontWeight: 600,
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      </StyledTableRow>
                    </Fade>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Box>
        </Fade>
      ))}
    </Container>
  );
};

export default StatusDashboard;
